import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import { navigateWithoutHistory } from "../utils/navigation";

const { KEYS: COOKIE_KEYS } = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.4,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
    },
  },
};

// 인앱웹뷰 감지 함수
const isInAppBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /wv|KAKAOTALK|Line|NAVER|FBAN|Instagram/.test(userAgent);
};

// 웹 환경 감지 함수 추가
const isWebEnvironment = () => {
  return window.innerWidth >= 768;
};

function IdCardSubmitPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [usingFrontCamera, setUsingFrontCamera] = useState(false);
  const [isInApp, setIsInApp] = useState(false);
  const [isWeb, setIsWeb] = useState(false); // 웹 환경 감지 상태 추가
  const [permissionStatus, setPermissionStatus] = useState("prompt"); // 'prompt', 'granted', 'denied'
  const [cameraError, setCameraError] = useState(null);
  const [useAlternativeInput, setUseAlternativeInput] = useState(false);
  const [showCamera, setShowCamera] = useState(false); // 카메라 UI 표시 상태 추가
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const topRef = useRef(null);

  // DOM 업데이트 전에 스크롤 초기화 (useLayoutEffect 사용)
  useLayoutEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    }
  }, []);

  // 컴포넌트 마운트 시 인앱 브라우저 확인 및 localStorage에서 이미지 로드
  useEffect(() => {
    // 스크롤 비활성화
    // document.body.style.overflow = "hidden";

    // 웹 환경 감지
    const webEnvironment = isWebEnvironment();
    setIsWeb(webEnvironment);

    // 웹 환경이면 항상 대체 입력 방식(파일 업로드) 사용
    if (webEnvironment) {
      setUseAlternativeInput(true);
    }

    // localStorage에서 임시 이미지 확인
    try {
      const storedImage = localStorage.getItem("temp_captured_image");
      if (storedImage) {
        setCapturedImage(storedImage);
        setButtonsVisible(true);
      }
    } catch (err) {}

    // 이전 라우터 상태에서 이미지도 함께 확인 (대체 방법)
    if (location.state && location.state.capturedImage) {
      setCapturedImage(location.state.capturedImage.imageDataURL);
      setButtonsVisible(true);
    }

    const inAppDetected = isInAppBrowser();
    setIsInApp(inAppDetected);

    // 인앱웹뷰에서는 대체 입력 방식 활성화
    if (inAppDetected) {
      setUseAlternativeInput(true);
    }

    // 브라우저에서 카메라 지원 여부 확인
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setUseAlternativeInput(true);
      return;
    }

    // 카메라 권한 상태 확인 (가능한 경우)
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "camera" })
        .then((status) => {
          setPermissionStatus(status.state);
          status.onchange = () => {
            setPermissionStatus(status.state);
          };
        })
        .catch((err) => {});
    }

    // 화면 크기 변경 감지
    const handleResize = () => {
      setIsWeb(isWebEnvironment());
    };

    window.addEventListener("resize", handleResize);

    // 컴포넌트 언마운트 시 카메라 정리 및 스크롤 복원
    return () => {
      document.body.style.overflow = "auto";
      stopCamera();
      window.removeEventListener("resize", handleResize);
    };
  }, [location]);

  // 전역 터치 이벤트 핸들러
  const handleGlobalTouch = (e) => {
    // 터치 이벤트 최적화를 위한 디버깅용 함수
  };

  // 카메라 시작 함수
  const startCamera = async (forceFront = false) => {
    // 대체 입력 모드인 경우 파일 입력으로 전환
    if (useAlternativeInput) {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    try {
      // 먼저 이전에 실행 중이던 카메라 중지
      stopCamera();

      // 디버깅용 로그 추가

      // 먼저 카메라 UI를 표시하여 videoRef 요소가 생성되도록 함
      setShowCamera(true);

      // 잠시 대기하여 DOM이 업데이트되도록 함
      await new Promise((resolve) => setTimeout(resolve, 100));

      // 여기서 비디오 요소가 존재하는지 다시 확인
      if (!videoRef.current) {
        // 약간의 지연 후 다시 시도
        setTimeout(() => {
          initializeCamera(forceFront);
        }, 300);
        return;
      }

      // 비디오 요소가 존재하면 카메라 초기화 진행
      await initializeCamera(forceFront);
    } catch (err) {
      handleCameraError(err, forceFront);
    }
  };

  // 카메라 초기화 함수 (startCamera에서 분리)
  const initializeCamera = async (forceFront = false) => {
    try {
      const facingMode = forceFront ? "user" : "environment";
      setUsingFrontCamera(forceFront);

      // 다양한 제약 조건 시도
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      // 인앱웹뷰에서 카메라 접근 시 안내 메시지 표시
      if (isInApp) {
        setError(
          "카메라 접근 권한을 요청합니다. 권한 요청 팝업이 표시되면 '허용'을 선택해주세요.",
        );
      }

      // 카메라 스트림 가져오기 시도
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // 스트림 참조 저장
      streamRef.current = stream;

      // 비디오 요소 확인 (한 번 더)
      if (!videoRef.current) {
        setCameraError(
          "비디오 요소를 찾을 수 없음 - 앱 재로드가 필요할 수 있습니다",
        );
        return;
      }

      // 비디오 요소에 스트림 연결
      videoRef.current.srcObject = stream;

      // 비디오 로드 및 재생 관련 이벤트 핸들러
      videoRef.current.onloadedmetadata = () => {
        videoRef.current
          .play()
          .then(() => {
            setCameraActive(true);
            setError(null);
            setCameraError(null);
          })
          .catch((err) => {
            setCameraError(`비디오 재생 실패: ${err.message}`);
          });
      };

      videoRef.current.onerror = (err) => {
        setCameraError(`비디오 요소 오류: ${err}`);
      };
    } catch (err) {
      handleCameraError(err, forceFront);
    }
  };

  // 카메라 오류 처리 함수
  const handleCameraError = (err, forceFront) => {
    setCameraError(`카메라 오류: ${err.name} - ${err.message}`);

    // 오류 메시지 상세화
    let errorMessage = "카메라 접근에 실패했습니다. ";

    if (err.name === "NotAllowedError") {
      errorMessage +=
        "카메라 접근 권한을 허용해주세요. 설정에서 권한을 변경할 수 있습니다.";
      setPermissionStatus("denied");
    } else if (err.name === "NotFoundError") {
      errorMessage += "카메라를 찾을 수 없습니다.";
    } else if (err.name === "NotReadableError") {
      errorMessage += "카메라가 이미 다른 앱에서 사용 중입니다.";
    } else {
      errorMessage += err.message || "알 수 없는 오류가 발생했습니다.";
    }

    // 인앱웹뷰에서 카메라 접근 실패 시 대체 입력 방식 활성화
    if (isInApp) {
      setUseAlternativeInput(true);
      errorMessage += " 이미지 업로드로 전환합니다.";
    }

    // 후면 카메라 실패 시 전면 카메라로 시도
    if (!forceFront && !useAlternativeInput) {
      try {
        startCamera(true);
        return;
      } catch (frontErr) {}
    }

    setError(errorMessage);
  };

  // 카메라 중지 함수
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setCameraActive(false);
    }
  };

  // 카메라 전환 함수
  const switchCamera = () => {
    startCamera(!usingFrontCamera);
  };

  // 사진 촬영 함수
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    try {
      // 비디오 크기에 맞게 캔버스 설정
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      // 비디오 프레임을 캔버스에 그리기
      const context = canvas.getContext("2d");

      // 전면 카메라의 경우 이미지 좌우 반전
      if (usingFrontCamera) {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // 가이드 영역 강조를 위한 레이어 추가
      if (usingFrontCamera) {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }

      // 캔버스 이미지를 데이터 URL로 변환
      const imageDataURL = canvas.toDataURL("image/jpeg");
      setCapturedImage(imageDataURL);

      // 카메라 중지
      stopCamera();
      setShowCamera(false); // 카메라 UI 숨기기
    } catch (err) {
      setError(`이미지 캡처 오류: ${err.message}`);
    }
  };

  // 카메라 페이지로 이동
  const goToCameraPage = () => {
    navigate("/id-card-camera");
  };

  // 이미지 파일 처리 함수 (파일 업로드 방식)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      // 크롭 페이지로 이동
      navigate("/id-card-crop", {
        state: {
          originalImage: event.target.result,
        },
      });
    };
    reader.readAsDataURL(file);
  };

  // 다시 촬영하기
  const handleRetakePhoto = () => {
    setCapturedImage(null);

    // 로컬 스토리지 이미지 제거
    localStorage.removeItem("temp_captured_image");

    // location.state 초기화 (history 객체 교체)
    window.history.replaceState(null, document.title, window.location.pathname);

    if (useAlternativeInput) {
      // 파일 입력 재설정
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
        fileInputRef.current.click();
      }
    } else {
      // 카메라 페이지로 이동
      goToCameraPage();
    }
  };

  const getPresignedUrls = async (files) => {
    try {
      const response = await axios.get(
        `${API_ENDPOINTS.BASE_URL}/mobile/file/upload`,
        {
          params: {
            type: "image",
            mimeType: "image/png",
            imageUploadTarget: "claimIdCardImage",
            num: files.length,
          },
          headers: {
            Authorization: `Bearer ${Cookies.get(COOKIE_KEYS.ACCESS_TOKEN)}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // 촬영된 신분증과 입력 정보를 확인하는 페이지로 이동
  const goToConfirm = () => {
    if (!capturedImage) {
      if (useAlternativeInput) {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      } else {
        goToCameraPage();
      }
      return;
    }

    // 확인 페이지에서 사용할 수 있도록 이미지를 저장
    try {
      localStorage.setItem("temp_captured_image", capturedImage);
    } catch (err) {}

    navigate("/submission-confirm");
  };

  const handleSubmitIdCard = async () => {
    if (!capturedImage) {
      if (useAlternativeInput) {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      } else {
        goToCameraPage();
      }
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      // 1. Base64 이미지를 File 객체로 변환
      const imageFile = await (async () => {
        const response = await fetch(capturedImage);
        const blob = await response.blob();
        return new File([blob], `idcard_${Date.now()}.png`, {
          type: "image/png",
        });
      })();

      // 2. Presigned URL 가져오기
      setUploadProgress(20);
      const presignedUrlData = await getPresignedUrls([imageFile]);

      if (!presignedUrlData || !presignedUrlData[0]?.url) {
        throw new Error("Presigned URL을 가져오는데 실패했습니다.");
      }

      setUploadProgress(40);

      // 3. S3에 업로드
      const presignedUrl = presignedUrlData[0].url;
      // S3 presigned URL 업로드를 위한 fetch 사용
      const s3Response = await fetch(presignedUrl, {
        method: "PUT",
        body: imageFile,
        headers: {
          "Content-Type": "image/png",
        },
      });

      if (!s3Response.ok) {
        throw new Error(
          `S3 업로드 실패: ${s3Response.status} ${s3Response.statusText}`,
        );
      }

      // 진행률 업데이트 (fetch는 진행률 추적이 어려우므로 고정값)
      setUploadProgress(80);

      // S3 URL 추출 및 이미지 경로만 사용
      const originalUrl = presignedUrl.split("?")[0];

      // 현재 날짜 기반으로 경로 생성 (YYYYMM)
      const date = new Date();
      const yearMonth = `${date.getFullYear()}${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}`;

      // 파일명 추출
      const fileName = originalUrl.split("/").pop();

      // 파일명이 비어 있으면 잘못된 경로가 만들어져 신분증 없는 완료 상태로 넘어갈 수 있음
      if (!fileName) {
        throw new Error("업로드된 파일 경로가 유효하지 않습니다.");
      }

      // 백엔드 요청 형식에 맞게 경로만 전송
      const imagePath = `images/origin/claimIdCardImage/${yearMonth}/${fileName}`;

      setUploadProgress(90);

      // 4. 신분증 이미지 URL을 서버에 전송
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/claimIdCardImage`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            claimIdCardImage: imagePath, // 전체 URL이 아닌 경로만 전송
          }),
        },
      );

      if (!response.ok) {
        throw new Error("신분증 제출에 실패했습니다.");
      }

      setUploadProgress(95);

      // 5. /claim/check API 호출 (실패해도 진행 — 단, 아래 검증에서 실제 저장 여부를 다시 확인)
      try {
        await axios.get(`${API_ENDPOINTS.BASE_URL}/mobile/claim/check`);
      } catch (checkError) {
        console.error("Claim Check API 호출 실패:", checkError);
      }

      // 5-1. 서버에 신분증이 실제로 저장되었는지 검증
      // /claim/check 가 실패해도 실제 데이터가 저장되어 있으면 완료로 진행하지만,
      // 저장이 안 된 경우에는 절대 완료 페이지로 넘어가지 않도록 막는다.
      const verifyResponse = await axios.get(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!verifyResponse.data || verifyResponse.data.claimIdCard !== true) {
        throw new Error(
          "신분증 저장이 확인되지 않았습니다. 다시 시도해주세요.",
        );
      }

      // 6. 공동소유자 정보가 있으면 POST /mobile/claim/share API 호출
      const buildingUnitInfo = Cookies.get("buildingUnitInfo");
      if (buildingUnitInfo) {
        try {
          const parsedBuildingUnitInfo = JSON.parse(buildingUnitInfo);
          const coOwners = parsedBuildingUnitInfo.coOwners || [];

          if (coOwners.length > 0) {
            // 각 공동소유자에 대해 API 호출
            for (const coOwner of coOwners) {
              try {
                await axios.post(
                  `${API_ENDPOINTS.BASE_URL}/mobile/claim/share`,
                  {
                    shareName: coOwner.name,
                    sharePhone: coOwner.phone,
                    shareBirth: coOwner.birthDate,
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      "Content-Type": "application/json",
                    },
                  },
                );
              } catch (shareError) {
                console.error(
                  `공동소유자 ${coOwner.name} API 호출 실패:`,
                  shareError,
                );
                // 개별 공동소유자 API 실패는 로그만 남기고 계속 진행
              }
            }
          }
        } catch (parseError) {
          console.error("공동소유자 정보 파싱 실패:", parseError);
        }
      }

      // 신분증 저장이 검증된 경우에만 완료 페이지로 이동
      setUploadProgress(100);
      setTimeout(() => {
        navigate("/completion");
      }, 500);
    } catch (error) {
      setUploadError("신분증을 재촬영후 다시 제출해주세요.");

      // 에러 모달 표시
      setShowErrorModal(true);

      // 3초 후에 에러 모달 닫기
      setTimeout(() => {
        setShowErrorModal(false);
      }, 3000);
    } finally {
      setIsUploading(false);
    }
  };

  // 이미지 로드 완료 핸들러 추가
  const handleImageLoad = () => {
    setImageLoaded(true);
    setButtonsVisible(true);
  };

  // 파일 업로드 안내 UI
  const renderFileUploadGuide = () => {
    if (useAlternativeInput && !capturedImage) {
      return (
        <div className="file-upload-guide">
          <h3>신분증 이미지 업로드</h3>
          <p>신분증 사진을 업로드해주세요</p>
          <p>
            카메라 촬영이 어려운 환경에서는 이미지 파일을 직접 선택할 수
            있습니다.
          </p>
          <button
            className="upload-button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 16L8.586 11.414C8.96103 11.0391 9.46967 10.8284 10 10.8284C10.5303 10.8284 11.039 11.0391 11.414 11.414L16 16M14 14L15.586 12.414C15.961 12.0391 16.4697 11.8284 17 11.8284C17.5303 11.8284 18.039 12.0391 18.414 12.414L20 14M14 8H14.01M6 20H18C18.5304 20 19.0391 19.7893 19.4142 19.4142C19.7893 19.0391 20 18.5304 20 18V6C20 5.46957 19.7893 4.96086 19.4142 4.58579C19.0391 4.21071 18.5304 4 18 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            이미지 선택하기
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: "none" }}
          />
        </div>
      );
    }
    return null;
  };

  // 신분증 이미지가 없는 경우 가이드 표시
  const renderIdCardGuide = () => {
    if (!capturedImage && !useAlternativeInput && !isWeb) {
      return (
        <div className="id-card-placeholder" style={{ marginTop: "10px" }}>
          <img
            src="/IdCard.png"
            alt="신분증 아이콘"
            className="id-card-icon"
            style={{
              width: 130,
              height: 130,
              marginBottom: "30px",
              marginTop: "20px",
            }}
          />
          <div
            style={{
              width: "100%",
              maxWidth: "360px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              marginBottom: "15px",
              padding: "0",
            }}
          >
            <img
              src="/idString1.png"
              alt="신분증 촬영 가이드"
              className="id-card-guide-string"
              style={{
                width: 297,
                marginBottom: "20px",
                padding: "0",
              }}
            />

            <img
              src="/idString.png"
              alt="신분증 촬영 가이드"
              className="id-card-guide-string"
              style={{
                width: 360,
                marginBottom: "15px",
              }}
            />
          </div>
        </div>
      );
    }
    return null;
  };

  // UI에서 이미지를 표시할 때는 state의 캡처된 이미지와 localStorage에서 전달받은 이미지 모두 확인
  const displayCapturedImage = capturedImage;

  return (
    <motion.div
      style={{
        padding: "25px",
        height: "100vh",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)",
      }}
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      // style={{
      //   paddingTop: "0",
      //   margin: 0,
      //   marginTop: "20px",
      //   width: "100%",
      //   minHeight: "100vh",
      //   overflow: "auto",
      //   WebkitOverflowScrolling: "touch",
      //   touchAction: "pan-y",
      //   display: "flex",
      //   flexDirection: "column",
      //   alignItems: "center",
      // }}
    >
      {/* 최상단 ref 요소 */}
      {/* <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "1px",
          height: "1px",
        }}
      /> */}

      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        style={{ marginTop: "0", marginBottom: "15px" }}
      >
        <h1>신분증 제출하기</h1>
      </motion.div>

      <div
        className="id-card-content"
        style={{
          marginTop: "0",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* 신분증 이미지 또는 가이드 */}
        {displayCapturedImage ? (
          <div
            className="id-card-image-container"
            style={{
              marginTop: "10px",
              paddingBottom: "20px",
            }}
          >
            <div className="id-card-preview">
              <img
                src={displayCapturedImage}
                alt="촬영된 신분증"
                onLoad={handleImageLoad}
                style={{
                  width: "100%",
                  height: "auto",
                  aspectRatio: "325/208",
                  objectFit: "contain",
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
                }}
              />
            </div>
          </div>
        ) : (
          <>
            {renderIdCardGuide()}
            {renderFileUploadGuide()}
          </>
        )}

        {error && <div className="error-message">{error}</div>}

        {/* 촬영된 이미지가 있는 경우 버튼 표시 */}
        {displayCapturedImage ? (
          <div
            className="id-card-buttons"
            style={{
              marginTop: "40px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              marginBottom: "40px",
              position: "relative",
              zIndex: 10,
            }}
          >
            <button
              className="retake-photo-button"
              onClick={handleRetakePhoto}
              style={{
                backgroundColor: "#EBEBFF",
                color: "#7A4FFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 20px",
                height: "56px",
                width: "100%",
                maxWidth: "400px",
                borderRadius: "8px",
                border: "none",
                fontSize: "16px",
                fontWeight: "700",
                margin: "0 auto",
                cursor: "pointer",
                WebkitTapHighlightColor: "rgba(0,0,0,0)",
                WebkitAppearance: "none",
                touchAction: "manipulation",
                userSelect: "none",
                WebkitUserSelect: "none",
                outline: "none",
              }}
            >
              <img
                src="/reset.png"
                alt="다시촬영"
                style={{
                  width: "20px",
                  height: "20px",
                  marginRight: "8px",
                  pointerEvents: "none",
                }}
              />
              {isWeb ? "다시 선택하기" : "다시 촬영하기"}
            </button>
            <button
              className="submit-photo-button"
              onClick={goToConfirm}
              style={{
                backgroundColor: "#7A4FFF",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 20px",
                height: "56px",
                width: "100%",
                maxWidth: "400px",
                borderRadius: "8px",
                border: "none",
                fontSize: "16px",
                fontWeight: "700",
                margin: "0 auto",
                cursor: "pointer",
                WebkitTapHighlightColor: "rgba(0,0,0,0)",
                WebkitAppearance: "none",
                touchAction: "manipulation",
                userSelect: "none",
                WebkitUserSelect: "none",
                outline: "none",
              }}
            >
              {/* <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: "8px", pointerEvents: "none" }}
              >
                <path
                  d="M5 13L9 17L19 7"
                  stroke="#FFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg> */}
              신분증 제출하기
            </button>
          </div>
        ) : (
          <div
            style={{
              marginTop: "30px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              marginBottom: "40px",
            }}
          >
            {!isWeb && ( // 웹 환경이 아닐 때만 촬영 버튼 표시
              <button
                className="take-photo-button"
                onClick={handleSubmitIdCard}
                style={{
                  backgroundColor: "#EBEBFF",
                  color: "#7A4FFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 20px",
                  height: "56px",
                  width: "100%",
                  maxWidth: "400px",
                  borderRadius: "8px",
                  border: "none",
                  fontSize: "16px",
                  fontWeight: "700",
                  margin: "0 auto",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "rgba(0,0,0,0)",
                  WebkitAppearance: "none",
                  touchAction: "manipulation",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  outline: "none",
                }}
              >
                <img
                  src="/Camera.png"
                  alt="카메라"
                  style={{
                    width: "24px",
                    height: "24px",
                    marginRight: "10px",
                    pointerEvents: "none",
                  }}
                />
                신분증 촬영하기
              </button>
            )}
            {!useAlternativeInput && ( // 파일 업로드 가이드가 없을 때 앨범 선택 버튼 표시
              <button
                className="select-album-button"
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                    fileInputRef.current.click();
                  }
                }}
                style={{
                  backgroundColor: "#FFFFFF",
                  color: "#7A4FFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 20px",
                  height: "56px",
                  width: "100%",
                  maxWidth: "400px",
                  borderRadius: "8px",
                  border: "1px solid #7A4FFF",
                  fontSize: "16px",
                  fontWeight: "700",
                  margin: "12px auto 0",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "rgba(0,0,0,0)",
                  WebkitAppearance: "none",
                  touchAction: "manipulation",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  outline: "none",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "10px", pointerEvents: "none" }}
                >
                  <path
                    d="M4 16L8.586 11.414C8.96103 11.0391 9.46967 10.8284 10 10.8284C10.5303 10.8284 11.039 11.0391 11.414 11.414L16 16M14 14L15.586 12.414C15.961 12.0391 16.4697 11.8284 17 11.8284C17.5303 11.8284 18.039 12.0391 18.414 12.414L20 14M14 8H14.01M6 20H18C18.5304 20 19.0391 19.7893 19.4142 19.4142C19.7893 19.0391 20 18.5304 20 18V6C20 5.46957 19.7893 4.96086 19.4142 4.58579C19.0391 4.21071 18.5304 4 18 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20Z"
                    stroke="#7A4FFF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                앨범에서 선택하기
              </button>
            )}
          </div>
        )}

        {/* 파일 업로드 hidden input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          style={{ display: "none" }}
        />
      </div>

      {isUploading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "12px",
              width: "80%",
              maxWidth: "300px",
            }}
          >
            <div style={{ marginBottom: "15px", textAlign: "center" }}>
              업로드 중... {uploadProgress}%
            </div>
            <div
              style={{
                width: "100%",
                height: "4px",
                backgroundColor: "#e0e0e0",
                borderRadius: "2px",
              }}
            >
              <div
                style={{
                  width: `${uploadProgress}%`,
                  height: "100%",
                  backgroundColor: "#7A4FFF",
                  borderRadius: "2px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showErrorModal && (
        <motion.div
          className="error-modal"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          <p>{uploadError}</p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default IdCardSubmitPage;
